import React from 'react';
import * as yup from 'yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Checkbox from '@mui/material/Checkbox';

import {
  ANALYSIS_TYPES,
  BG_BLANKET_ACCOUNT,
  FORM_TITLES,
  MARKUP_TYPES,
  PULL_SEND_REQUEST_TYPES,
  RATE_TYPES,
  REQUEST_TYPES,
} from '../constants';
import { StatesField, SubmissionButton, ZipCodeField } from '../../components';
import { useAuth } from '../../auth';
import { CONTAINERS } from '../../../../shared-jarvis';
import {
  AnalysisTypeField,
  CarrierListField,
  CarrierTiersField,
  EnterpriseSearchField,
  FileNameField,
  ImpactScacRestrictionsField,
  MarkupAmountField,
  MarkupPercentageTypeField,
  MarkupTypeField,
  NotifyEmailListField,
  PullDataDateRangeField,
  PullSendRequestTypeField,
  RateTypeField,
  RequestTypesField,
} from '../fields';
import { pullAndSendFieldSchema } from '../pullAndSendFieldSchema';
import { pullAndSendColumnBuilders } from './workbookColumnBuilders';
import {
  withUploadToBlobStorage,
  WithUploadToBlobStorageProps,
} from '../withUploadToBlobStorage';

const schema = yup.object({
  ...pullAndSendFieldSchema,
});

export type PullAndSendFormFields = yup.InferType<typeof schema>;

const Form = ({
  handleFormSubmit,
  submitting,
}: WithUploadToBlobStorageProps<PullAndSendFormFields>) => {
  const auth = useAuth();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    resetField,
  } = useForm<PullAndSendFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      fileName: '',
      pullSendRequestType: PULL_SEND_REQUEST_TYPES.BATCH_RATE,
      dateRange: {
        startDate: undefined,
        endDate: undefined,
      },
      accounts: [],
      notifyEmails: auth.user?.email ? [{ value: auth.user.email }] : [],
      // Batch-type defaults
      returnCustomerRates: true,
      returnLiveContractRates: true,
      returnProposalContractRates: false,
      returnVolumeRates: false,
      targetedCarriers: [],
      // Rating Analysis defaults
      requestType: REQUEST_TYPES.ASP,
      rateType: RATE_TYPES.CARRIER,
      analysisType: ANALYSIS_TYPES.LEAST_COST_CARRIER,
      carrierTiers: [],
      carrierExclusions: [],
      carrierInclusions: [],
      originStateExclusions: [],
      destinationStateExclusions: [],
      markupType: MARKUP_TYPES.FLAT,
      markupAmount: '',
    },
    shouldUnregister: true,
  });

  const selectedPullSendRequestType = watch('pullSendRequestType');
  const selectedRequestType = watch('requestType');
  const selectedRateType = watch('rateType');
  const selectedMarkupType = watch('markupType');
  const selectedAnalysisType = watch('analysisType');
  const selectedAccounts = watch('accounts');
  const selectedCarrierTiers = watch('carrierTiers');

  const isRatingAnalysis =
    selectedPullSendRequestType === PULL_SEND_REQUEST_TYPES.RATING_ANALYSIS;
  const isBatchType =
    selectedPullSendRequestType === PULL_SEND_REQUEST_TYPES.BATCH_RATE ||
    selectedPullSendRequestType === PULL_SEND_REQUEST_TYPES.AP_TESTING ||
    selectedPullSendRequestType === PULL_SEND_REQUEST_TYPES.RATE_COMPARE ||
    selectedPullSendRequestType === PULL_SEND_REQUEST_TYPES.GEO_MILEAGE;

  // Request Type selection side-effects
  React.useEffect(() => {
    if (selectedRequestType === REQUEST_TYPES.BLANKETS) {
      setValue('rateType', RATE_TYPES.CARRIER);
      setValue('accounts', [BG_BLANKET_ACCOUNT]);
      resetField('markupType');
    }

    if (selectedRequestType === REQUEST_TYPES.ASP) {
      setValue('markupType', MARKUP_TYPES.CURRENT);
      setValue(
        'accounts',
        selectedAccounts.filter(
          (selectedAccount) =>
            selectedAccount?.accountNumber !== BG_BLANKET_ACCOUNT.accountNumber
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedAccounts intentionally omitted: effect should only run on requestType change, not on every account selection
  }, [selectedRequestType, setValue, resetField]);

  const onSubmit: SubmitHandler<PullAndSendFormFields> = async (formData) => {
    // All pull-based requests use the RETRIEVAL container
    // The request type is specified in the form data/column builders
    handleFormSubmit(
      formData,
      CONTAINERS.RATEQUALITY_SHIPMENTS_RETRIEVAL,
      pullAndSendColumnBuilders
    );
  };

  return (
    <>
      <Typography
        align="center"
        component="h1"
        variant="h4"
        sx={{ mb: 3, mt: 2 }}
      >
        {FORM_TITLES.PULL_AND_SEND_REQUEST}
      </Typography>

      <form
        style={{ marginTop: '1rem', marginBottom: 0 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <FileNameField<PullAndSendFormFields>
          control={control}
          name="fileName"
          error={errors.fileName}
        />

        <PullSendRequestTypeField<PullAndSendFormFields>
          control={control}
          name="pullSendRequestType"
          error={errors.pullSendRequestType}
        />

        <PullDataDateRangeField<PullAndSendFormFields>
          control={control}
          name="dateRange"
          error={errors.dateRange}
        />

        <EnterpriseSearchField<PullAndSendFormFields>
          control={control}
          error={errors.accounts as any}
          helperText="Search by Account Name or Number"
          label={isBatchType ? 'Rating Account' : 'Account'}
          multiple={false}
          name="accounts"
          user={auth.user!}
        />

        {/* Batch-type fields (Batch Rate, AP Testing, Rate Compare, GEO Mileage) */}
        {isBatchType && (
          <>
            <Box sx={{ mt: 3, mb: 2 }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Rate Return Options
              </FormLabel>
              <FormGroup>
                <Controller
                  name="returnCustomerRates"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Return Customer Rates"
                    />
                  )}
                />
                <Controller
                  name="returnLiveContractRates"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Return Live Contract Rates"
                    />
                  )}
                />
                <Controller
                  name="returnProposalContractRates"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Return Proposal Contract Rates"
                    />
                  )}
                />
                <Controller
                  name="returnVolumeRates"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      }
                      label="Return Volume Rates"
                    />
                  )}
                />
              </FormGroup>
            </Box>

            <CarrierListField<PullAndSendFormFields>
              control={control}
              error={errors.targetedCarriers}
              label="Targeted Carriers (Optional)"
              name="targetedCarriers"
            />
          </>
        )}

        {/* Rating Analysis fields */}
        {isRatingAnalysis && (
          <>
            <RequestTypesField<PullAndSendFormFields>
              control={control}
              name="requestType"
              error={errors.requestType}
            />

            <RateTypeField<PullAndSendFormFields>
              disabled={selectedRequestType === REQUEST_TYPES.BLANKETS}
              control={control}
              name="rateType"
              error={errors.rateType}
            />

            <CarrierTiersField<PullAndSendFormFields>
              control={control}
              name="carrierTiers"
              error={errors.carrierTiers}
            />

            <CarrierListField<PullAndSendFormFields>
              control={control}
              error={errors.carrierExclusions}
              filterOptions={(options) =>
                options.filter((opt) =>
                  selectedCarrierTiers?.includes(opt.carrier.tier)
                )
              }
              label="Exclude Carriers"
              name="carrierExclusions"
            />

            <CarrierListField<PullAndSendFormFields>
              control={control}
              error={errors.carrierExclusions}
              filterOptions={(options) =>
                options.filter(
                  (opt) => !selectedCarrierTiers?.includes(opt.carrier.tier)
                )
              }
              label="Include Carriers"
              name="carrierInclusions"
            />
          </>
        )}

        {isRatingAnalysis && (
          <>
            <AnalysisTypeField<PullAndSendFormFields>
              control={control}
              name="analysisType"
              error={errors.analysisType}
            />

            <ImpactScacRestrictionsField<PullAndSendFormFields>
              control={control}
              error={errors.impactScacRestrictions}
              hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
              name="impactScacRestrictions"
            />

            <ZipCodeField<PullAndSendFormFields>
              control={control}
              error={errors.originZipExclusions}
              hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
              label="Origin Zip Exclusions"
              name="originZipExclusions"
            />

            <StatesField<PullAndSendFormFields>
              control={control}
              name="originStateExclusions"
              label="Origin State/Province Exclusions"
              hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
            />

            <ZipCodeField<PullAndSendFormFields>
              control={control}
              error={errors.destinationZipExclusions}
              hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
              label="Destination Zip Exclusions"
              name="destinationZipExclusions"
            />

            <StatesField<PullAndSendFormFields>
              control={control}
              name="destinationStateExclusions"
              label="Destination State/Province Exclusions"
              hidden={selectedAnalysisType !== ANALYSIS_TYPES.IMPACT_ANALYSIS}
            />

            <MarkupTypeField<PullAndSendFormFields>
              control={control}
              error={errors.markupType}
              filterOptions={(options) => {
                if (selectedRequestType === REQUEST_TYPES.BLANKETS) {
                  return options.filter((o) => o.value !== MARKUP_TYPES.CURRENT);
                }

                return options;
              }}
              hidden={selectedRateType !== RATE_TYPES.CARRIER}
              name="markupType"
            />

            <MarkupPercentageTypeField<PullAndSendFormFields>
              control={control}
              hidden={selectedMarkupType !== MARKUP_TYPES.PERCENT}
              name="markupPercentType"
              error={errors.markupPercentType}
            />

            <MarkupAmountField<PullAndSendFormFields>
              control={control}
              name="markupAmount"
              error={errors.markupAmount}
              amountType={
                selectedMarkupType === MARKUP_TYPES.PERCENT
                  ? 'Percentage'
                  : 'Dollar'
              }
              hidden={
                selectedRateType !== RATE_TYPES.CARRIER ||
                selectedMarkupType === MARKUP_TYPES.CURRENT
              }
            />
          </>
        )}

        <NotifyEmailListField<PullAndSendFormFields>
          control={control}
          error={errors.notifyEmails}
          name="notifyEmails"
          hidden={!auth.isBGEmployee}
        />

        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>Please fix the following errors:</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {errors.fileName && <li>{errors.fileName.message}</li>}
              {errors.pullSendRequestType && <li>{errors.pullSendRequestType.message}</li>}
              {errors.dateRange?.startDate && <li>Start date is required</li>}
              {errors.dateRange?.endDate && <li>End date is required</li>}
              {errors.requestType && <li>{errors.requestType.message}</li>}
              {errors.rateType && <li>{errors.rateType.message}</li>}
              {errors.accounts && (
                <li>{(errors.accounts as any).message || 'At least one account is required'}</li>
              )}
              {errors.carrierTiers && <li>{errors.carrierTiers.message}</li>}
              {errors.analysisType && <li>{errors.analysisType.message}</li>}
              {errors.markupType && <li>{errors.markupType.message}</li>}
              {errors.markupPercentType && <li>{errors.markupPercentType.message}</li>}
              {errors.markupAmount && <li>{errors.markupAmount.message}</li>}
              {errors.notifyEmails && <li>Valid notification email is required</li>}
            </ul>
          </Alert>
        )}

        <SubmissionButton
          loading={submitting}
          buttonProps={{
            sx: { mt: 3, p: 2 },
            fullWidth: true,
            type: 'submit',
          }}
        />
      </form>
    </>
  );
};

export const PullAndSendForm = withUploadToBlobStorage(Form);
